import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CardSkeleton, PageHeader, PageHeaderSkeleton, StatCardsSkeleton } from '@/components/core';
import { useGetPmReviewById } from '@/modules/performance/hr-review-core';
import { ComparisonTable } from '../../components/comparison-table/comparison-table';
import { HealthStrip } from '../../components/health-strip/health-strip';
import { HeroSummary } from '../../components/hero-summary/hero-summary';
import { KpiStrip } from '../../components/kpi-strip/kpi-strip';
import { QuantitativeScoring } from '../../components/quantitative-scoring/quantitative-scoring';
import { ScoreOverview } from '../../components/score-overview/score-overview';
import { StrengthsImprovements } from '../../components/strengths-improvements/strengths-improvements';
import { WeightedCategoryBars } from '../../components/weighted-category-bars/weighted-category-bars';
import { useDashboardAccess, useGetPerformanceDashboard } from '../../hooks/use-dashboard';

/** The dashboard's sections (summary, KPI strip, the two charts) as placeholders. */
const DashboardSkeleton = () => (
  <div className="flex flex-col gap-4" aria-busy="true">
    <CardSkeleton lines={2} />
    <StatCardsSkeleton count={4} />
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <CardSkeleton bodyClassName="h-56 w-full" />
      <CardSkeleton bodyClassName="h-56 w-full" />
    </div>
  </div>
);

/**
 * `/performance/all-reviews/:reviewId/dashboard` (Phase 6): the performance dashboard of the review's
 * employee, opened from the review's details instead of the menu, over all their cycles. Who may see it is
 * unchanged: admins any employee, a department head their department, nobody their own.
 */
export const ReviewDashboardPage = () => {
  const { t } = useTranslation();
  const { reviewId } = useParams<{ reviewId: string }>();
  const { data: review, isLoading: isReviewLoading } = useGetPmReviewById(reviewId);
  const employeeId = review?.ReviewForId ?? '';

  const access = useDashboardAccess(employeeId);
  const { data: dashboard, isLoading } = useGetPerformanceDashboard(employeeId, access.canView);

  if (isReviewLoading || (employeeId && access.isResolving)) {
    return (
      <div className="flex w-full flex-col gap-4">
        <PageHeaderSkeleton />
        <DashboardSkeleton />
      </div>
    );
  }

  if (!review) {
    return <p className="text-sm text-muted-foreground">{t('REVIEW_NOT_FOUND')}</p>;
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('PERFORMANCE_DASHBOARD')} description={review.Name} />

      {!access.canView && (
        <p className="text-sm text-muted-foreground">
          {t(access.scope === 'none' ? 'NOT_AUTHORIZED_TO_VIEW_DASHBOARD' : 'CANNOT_VIEW_THIS_DASHBOARD')}
        </p>
      )}

      {access.canView && isLoading && <DashboardSkeleton />}
      {access.canView && !isLoading && !dashboard && (
        <p className="text-sm text-muted-foreground">{t('NO_REVIEW_DATA_AVAILABLE')}</p>
      )}

      {dashboard && access.canView && (
        <>
          <HeroSummary dashboard={dashboard} />
          <KpiStrip dashboard={dashboard} />
          {dashboard.IsQuantitativeEligible && <HealthStrip dashboard={dashboard} />}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 [&>*]:min-w-0">
            <ScoreOverview dashboard={dashboard} />
            <WeightedCategoryBars dashboard={dashboard} />
          </div>

          {dashboard.IsQuantitativeEligible && <QuantitativeScoring dashboard={dashboard} />}
          <StrengthsImprovements dashboard={dashboard} />
          <ComparisonTable dashboard={dashboard} />
        </>
      )}
    </div>
  );
};
